import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import BookingClosedNotice from '../../components/notices/BookingClosedNotice';
import { extractError } from '../../utils';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginError {
  message: string;
  type: 'error' | 'warning';
  icon?: string;
}

const LoginPage: React.FC = () => {
  usePageTitle('Student Sign In — Easy and Quiet Hostel');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, logout } = useAuth();
  const { bookingWindow, loading: brandLoading } = useBrand();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const redirectParam = searchParams.get('redirect');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields },
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const emailValue = watch('email');
  const forgotPasswordHref = emailValue?.trim()
    ? `/forgot-password?email=${encodeURIComponent(emailValue.trim())}`
    : '/forgot-password';

  const getErrorDetails = (errorMessage: string): LoginError => {
    // Handle specific error messages from backend
    if (errorMessage.includes('No account found')) {
      return {
        message: 'No account found with this email address. Please check your email or sign up for a new account.',
        type: 'error',
        icon: '👤'
      };
    }
    
    if (errorMessage.includes('Incorrect password')) {
      return {
        message: 'The password you entered is incorrect. Please try again or reset your password.',
        type: 'error',
        icon: '🔒'
      };
    }
    
    if (errorMessage.includes('deactivated')) {
      return {
        message: 'Your account has been deactivated. Please contact an administrator for assistance.',
        type: 'warning',
        icon: '⚠️'
      };
    }
    
    if (errorMessage.includes('already exists')) {
      return {
        message: 'An account with this email already exists. Please try logging in instead.',
        type: 'error',
        icon: '📧'
      };
    }

    // Default error
    return {
      message: errorMessage || 'Login failed. Please check your credentials and try again.',
      type: 'error',
      icon: '❌'
    };
  };

  const resolveRedirect = (redirect: string | null) => {
    if (!redirect) {
      return '/student/dashboard';
    }

    try {
      const decoded = decodeURIComponent(redirect);
      return decoded.startsWith('/') ? decoded : '/student/dashboard';
    } catch (_error) {
      return '/student/dashboard';
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      await login(data);

      const savedUser = localStorage.getItem('user');
      const role = savedUser ? JSON.parse(savedUser)?.role : null;

      if (role !== 'STUDENT') {
        logout();
        setError({
          message: 'This page is for student accounts. Use admin sign in for admin access.',
          type: 'warning',
          icon: '⚠️',
        });
        return;
      }

      navigate(resolveRedirect(redirectParam));
    } catch (err: any) {
      const errorMessage = extractError(err, 'Login failed. Please try again.');
      setError(getErrorDetails(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle browser autofill detection
  useEffect(() => {
    const checkAutofill = () => {
      const emailInput = document.getElementById('email-address') as HTMLInputElement;
      const passwordInput = document.getElementById('password') as HTMLInputElement;
      
      // Only set values if they're autofilled and the form field is still empty
      if (emailInput?.value && !errors.email) {
        setValue('email', emailInput.value, { shouldValidate: false, shouldTouch: false });
      }
      
      if (passwordInput?.value && !errors.password) {
        setValue('password', passwordInput.value, { shouldValidate: false, shouldTouch: false });
      }
    };

    // Check immediately and after a delay for autofill
    checkAutofill();
    const timer = setTimeout(checkAutofill, 200);

    return () => clearTimeout(timer);
  }, [setValue, errors.email, errors.password]);

  // The booking window defaults to closed until the settings endpoint answers,
  // so wait for it — rendering the gate first would flash "sign in is closed"
  // at every visitor on every cold load.
  if (brandLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary px-4 py-12">
        <LoadingSpinner size="lg" label="Loading sign in" />
      </div>
    );
  }

  // Student sign-in only. Staff keep their own entry point at /admin/login,
  // which this never gates — the settings page that reopens the platform is
  // behind it.
  if (!bookingWindow.signInOpen) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-4 py-12">
        <BookingClosedNotice headline="Student Sign In is Closed" />
        <Link
          to="/admin/login"
          className="mt-6 text-xs text-muted-foreground underline-offset-2 hover:underline"
        >
          Staff sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Student booking and payment portal
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <Input
              id="email-address"
              label="Email address"
              type="email"
              autoComplete="email"
              {...register('email')}
              error={touchedFields.email ? errors.email?.message : undefined}
            />
            <Input
              id="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
              error={touchedFields.password ? errors.password?.message : undefined}
            />
            <div className="text-right">
              <Link
                to={forgotPasswordHref}
                className="text-xs font-medium text-brand hover:text-brand-light"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          {error && (
            <div className={`rounded-lg border-l-4 p-4 ${
              error.type === 'warning'
                ? 'bg-warning-subtle border-status-reserved'
                : 'bg-destructive-subtle border-status-overdue'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {error.icon && (
                    <span className="text-lg" role="img" aria-label="Error icon">
                      {error.icon}
                    </span>
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    error.type === 'warning' ? 'text-warning-foreground' : 'text-rose-800 dark:text-rose-200'
                  }`}>
                    {error.message}
                  </p>
                  {error.message.includes('Incorrect password') && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground">
                        Tip: Make sure Caps Lock is off and check for any extra spaces.
                      </p>
                    </div>
                  )}
                  {error.message.includes('No account found') && bookingWindow.registrationOpen && (
                    <div className="mt-2">
                      <Link
                        to={redirectParam ? `/register?redirect=${encodeURIComponent(redirectParam)}` : '/register'}
                        className="text-xs text-brand hover:text-brand-light underline"
                      >
                        Create a new account
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <Button
              type="submit"
              loading={isLoading}
              className="w-full"
              size="lg"
            >
              Sign in
            </Button>
          </div>

          <div className="text-center">
            {bookingWindow.registrationOpen ? (
              <p className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link
                  to={redirectParam ? `/register?redirect=${encodeURIComponent(redirectParam)}` : '/register'}
                  className="font-medium text-brand hover:text-brand-light"
                >
                  Sign up
                </Link>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                New accounts are closed for now.{' '}
                <Link to="/support" className="font-medium text-brand hover:text-brand-light">
                  Contact support
                </Link>
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
